import { useEffect, useRef, useState } from 'react'

export const WINDOW_MS = 30 * 60 * 1000


const SERIES_COLORS = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834']
export const seriesColor = (index) => SERIES_COLORS[index % SERIES_COLORS.length]

const formatTime = (epochSec) => {
  const d = new Date(epochSec * 1000)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

// Formats a numeric x-axis tick label for the charts' epochMs axis. Time-only
// (HH:MM, no seconds) when the visible range fits in a day; adds the date
// once it spans more, so same-time-of-day points on different days (e.g. a
// multi-day range) are never ambiguous on the axis.
export const formatChartTimestamp = (epochMs, rangeMs) => {
  const d = new Date(epochMs)
  if (rangeMs !== undefined && rangeMs > 24 * 60 * 60 * 1000) {
    return d.toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

// Hover-tooltip timestamp — always full date + seconds precision (unlike the
// axis label above, this doesn't abbreviate based on the selected range),
// matching the Figma hover-tooltip spec: "DD-MM-YYYY  HH:MM:SS".
export const formatTooltipTimestamp = (epochMs) => {
  const d = new Date(epochMs)
  const pad = (n) => String(n).padStart(2, '0')
  const date = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  return `${date}  ${time}`
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

// Candidate tick spacings, smallest to largest — all clean, human-recognizable
// units (never an odd number of minutes/hours).
const TICK_INTERVALS_MS = [
  MINUTE_MS, 2 * MINUTE_MS, 3 * MINUTE_MS, 5 * MINUTE_MS, 10 * MINUTE_MS, 15 * MINUTE_MS, 20 * MINUTE_MS, 30 * MINUTE_MS,
  HOUR_MS, 2 * HOUR_MS, 3 * HOUR_MS, 6 * HOUR_MS, 12 * HOUR_MS,
  DAY_MS, 2 * DAY_MS, 7 * DAY_MS, 14 * DAY_MS, 30 * DAY_MS,
]

// Rounds an epochMs DOWN to a clean LOCAL-time boundary for the given
// interval (local hour/minute marks for sub-day intervals, local midnight
// for day-or-longer ones) — so e.g. a 2-hour interval lands on 12:00, 14:00,
// 16:00 in the viewer's own timezone, not arbitrary offsets from the raw
// UTC epoch (which would be wrong for any timezone with a half-hour offset).
const floorToLocalBoundary = (ms, intervalMs) => {
  const d = new Date(ms)
  if (intervalMs >= DAY_MS) {
    d.setHours(0, 0, 0, 0)
    const days = Math.round(intervalMs / DAY_MS)
    if (days > 1) {
      const dayIndex = Math.floor(d.getTime() / DAY_MS)
      d.setTime((dayIndex - (dayIndex % days)) * DAY_MS)
    }
    return d.getTime()
  }
  if (intervalMs >= HOUR_MS) {
    const hours = intervalMs / HOUR_MS
    d.setMinutes(0, 0, 0)
    d.setHours(d.getHours() - (d.getHours() % hours))
    return d.getTime()
  }
  const minutes = intervalMs / MINUTE_MS
  d.setSeconds(0, 0)
  d.setMinutes(d.getMinutes() - (d.getMinutes() % minutes))
  return d.getTime()
}

// Generates explicit x-axis tick positions for a [fromMs, toMs] domain,
// picking whichever candidate interval keeps the tick count at or under
// targetCount — so "Last 1 Day" gets ~8 ticks at 3-hour intervals (24h / 8 =
// 3h), "Last 1 hr" ticks every 10 min, "Last 7 days" every day, etc.,
// scaling with whatever range is selected instead of a fixed spacing.
export const computeAxisTicks = (fromMs, toMs, targetCount = 8) => {
  const span = toMs - fromMs
  if (!(span > 0)) return undefined
  const interval = TICK_INTERVALS_MS.find((ms) => span / ms <= targetCount) ?? TICK_INTERVALS_MS[TICK_INTERVALS_MS.length - 1]
  const ticks = []
  for (let t = floorToLocalBoundary(fromMs, interval); t <= toMs; t += interval) {
    if (t >= fromMs) ticks.push(t)
  }
  return ticks
}

export const useTelemetryHistory = (timestampSec, values) => {
  const pointsRef = useRef([])
  const lastTsRef = useRef(null)
  const valuesRef = useRef(values)
  valuesRef.current = values
  const [, forceRender] = useState(0)

  useEffect(() => {
    if (timestampSec === undefined || timestampSec === null) return
    if (timestampSec === lastTsRef.current) return
    lastTsRef.current = timestampSec

    const epochMs = timestampSec * 1000
    const point = { time: formatTime(timestampSec), epochMs, ...valuesRef.current }
    pointsRef.current = [...pointsRef.current, point].filter((p) => epochMs - p.epochMs <= WINDOW_MS)
    forceRender((n) => n + 1)
  }, [timestampSec])

  return pointsRef.current
}

export const mergeIntoWindow = (seedPoints, livePoints) => {
  const byTs = new Map()
  seedPoints.forEach((p) => byTs.set(p.epochMs, p))
  livePoints.forEach((p) => byTs.set(p.epochMs, p))
  const merged = Array.from(byTs.values()).sort((a, b) => a.epochMs - b.epochMs)
  if (merged.length === 0) return merged
  const newest = merged[merged.length - 1].epochMs
  return merged.filter((p) => newest - p.epochMs <= WINDOW_MS)
}

// Recharts renders every point it's given as SVG and freezes (or OOMs the
// tab) somewhere past ~10k — long history ranges can carry 100k+ snapshots,
// so charts get an evenly-strided sample capped at this many points. The
// legend and the All Data table still consume the full set.
export const MAX_CHART_POINTS = 600

export const downsamplePoints = (points, maxPoints = MAX_CHART_POINTS) => {
  if (points.length <= maxPoints) return points
  const step = points.length / maxPoints
  const out = []
  for (let i = 0; i < maxPoints; i += 1) out.push(points[Math.floor(i * step)])
  out[out.length - 1] = points[points.length - 1] // never drop the newest point
  return out
}

/**
 * Builds the { points, legend } shape TelemetryChart.jsx expects from a
 * rolling history buffer. seriesDefs: [{ key, unit, scale }] -- key must
 * match a property name in each history point; unit is appended as-is to
 * Latest/Min/Max (include your own leading space, e.g. ' V'). scale is an
 * optional multiplier applied to every value for that key (e.g. 0.001 to
 * chart mV data in V) — omit/1 for no conversion. Rounded to 3 decimals
 * after scaling so a plain integer input (mV) can't leave float noise like
 * 3.2039999999999998 in the legend/tooltip.
 *
 * Legend stats (latest/min/max) are computed over ALL points; only the
 * points handed to the chart are downsampled.
 */
export const buildTelemetryChart = (points, seriesDefs) => {
  // Cheap path for the common case (no seriesDef sets a scale): reuse the
  // original point objects untouched, same as before this was added.
  const scaledPoints = points.map((p) => {
    let out = p
    seriesDefs.forEach((def) => {
      if (!def.scale || def.scale === 1) return
      const v = p[def.key]
      if (typeof v !== 'number') return
      if (out === p) out = { ...p }
      out[def.key] = Math.round(v * def.scale * 1000) / 1000
    })
    return out
  })
  const legend = seriesDefs.map((def, idx) => {
    // Single pass instead of Math.min(...values) — spreading 100k+ elements
    // as arguments throws "Maximum call stack size exceeded".
    let latest
    let min
    let max
    scaledPoints.forEach((p) => {
      const v = p[def.key]
      if (typeof v !== 'number' || Number.isNaN(v)) return
      latest = v
      if (min === undefined || v < min) min = v
      if (max === undefined || v > max) max = v
    })
    return { name: def.key, color: seriesColor(idx), unit: def.unit, latest, min, max }
  })
  return { points: downsamplePoints(scaledPoints), legend }
}
